import { Injectable } from '@angular/core';
import { createStore, select, setProps, withProps } from '@ngneat/elf';
import { persistState, sessionStorageStrategy } from '@ngneat/elf-persist-state';
import { createRequestDataSource, createRequestsStatusOperator, updateRequestStatus, withRequestsStatus } from '@ngneat/elf-requests';
import { AppError, RegisterRequest, RegistrationCguDto, RegistrationSiteDto } from '@timechef/core/api';
import { includeKeys, selectAppRequestStatus } from '@timechef/core/utils/elf-utils';
import {
  LinkAccount,
  RegistrationSite,
  RegistrationStep2,
  RegistrationStep3,
  RegistrationStep3Form,
  RegistrationStep4,
  RegistrationStep4Form,
} from './registration.models';

interface RegistrationProps {
  site: RegistrationSite | null;
  step2: RegistrationStep2 | null;
  step3: RegistrationStep3 | null;
  step4: RegistrationStep4 | null;
  cgu: RegistrationCguDto | null;
  emailIsValidated: boolean;
  linkAccount: LinkAccount | null;
}

export enum RegistrationStatusKey {
  LoadSite = 'load-site',
  LoadSites = 'load-sites',
  EnsureEmailValidity = 'ensure-email-validity',
  Register = 'register',
  Unregister = 'unregister',
  SendConfirmEmail = 'send-confirm-email',
  ConfirmEmail = 'confirm-email',
}

@Injectable({ providedIn: 'root' })
export class RegistrationRepository {
  private store = createStore(
    { name: 'registration' },
    withProps<RegistrationProps>({
      site: null,
      step2: null,
      step3: null,
      step4: null,
      cgu: null,
      emailIsValidated: false,
      linkAccount: null,
    }),
    withRequestsStatus<RegistrationStatusKey>(),
  );

  instance = persistState(this.store, {
    key: 'registration',
    storage: sessionStorageStrategy,
    source: () => this.store.pipe(includeKeys(['cgu', 'emailIsValidated', 'site', 'step2', 'step3', 'step4'])),
  });

  sitesDS = createRequestDataSource({
    store: this.store,
    data$: () =>
      this.store.pipe(
        select((s) => ({
          sites: s.site?.sites,
          isSitesNextToMe: s.site?.isSitesNextToMe,
        })),
      ),
    requestKey: RegistrationStatusKey.LoadSites,
    dataKey: 'data',
  });

  selectedSite$ = this.store.pipe(select((s) => s.site?.selectedSite));
  emailValidityRequestStatus$ = this.store.pipe(selectAppRequestStatus(RegistrationStatusKey.EnsureEmailValidity));
  registerRequestStatus$ = this.store.pipe(selectAppRequestStatus(RegistrationStatusKey.Register));
  step3$ = this.store.pipe(select((s) => s.step3));
  sendConfirmEmailRequestStatus$ = this.store.pipe(selectAppRequestStatus(RegistrationStatusKey.SendConfirmEmail));
  confirmEmailRequestStatus$ = this.store.pipe(selectAppRequestStatus(RegistrationStatusKey.ConfirmEmail));
  unregisterRequestStatus$ = this.store.pipe(selectAppRequestStatus(RegistrationStatusKey.Unregister));
  loadSiteRequestStatus$ = this.store.pipe(selectAppRequestStatus(RegistrationStatusKey.LoadSite));

  getSelectedSite = () => this.store.query((s) => s.site?.selectedSite);
  getStep2 = () => this.store.query((s) => s.step2);
  getStep3 = (): RegistrationStep3Form | null => this.store.query((s) => (s.step3 ? { ...s.step3, cgu: s.cgu } : null));
  getStep4 = (): { step4: RegistrationStep4 | null; cgu: RegistrationCguDto | null } => this.store.query((s) => ({ step4: s.step4, cgu: s.cgu }));
  getHasStep4 = () => this.store.query((s) => hasStep4(s.site?.selectedSite));
  getIsSitesNextToMe = () => this.store.query((s) => s.site?.isSitesNextToMe);
  getAllForRegister = () =>
    this.store.query((s) => ({
      site: s.site,
      step2: s.step2,
      step3: s.step3,
      step4: s.step4,
    }));

  getIsStep2Accessible = () => this.store.query((s) => !!s.site?.selectedSite);
  getIsStep3Accessible = () => this.getIsStep2Accessible() && this.store.query((s) => !!s.step2 && s.emailIsValidated);
  getIsStep4Accessible = () => this.getIsStep3Accessible() && this.store.query((s) => !!s.step3 && !!hasStep4(s.site?.selectedSite));

  setSelectedSite(selectedSite?: RegistrationSiteDto) {
    this.store.update(setProps({ site: { selectedSite } }), updateRequestStatus(RegistrationStatusKey.LoadSite, 'success'));
  }

  setSites(sites: RegistrationSiteDto[], isSitesNextToMe: boolean) {
    this.store.update(setProps({ site: { sites, isSitesNextToMe } }), updateRequestStatus(RegistrationStatusKey.LoadSites, 'success'));
  }

  clearSites() {
    this.store.update((s) => ({ ...s, site: { sites: undefined, isSitesNextToMe: false, selectedSite: s.site?.selectedSite } }));
  }

  resetUnregisterStatus() {
    this.store.update(updateRequestStatus(RegistrationStatusKey.Unregister, 'idle'));
  }

  submitStep2 = (step2: RegistrationStep2, emailIsValidated: boolean) => {
    if (emailIsValidated) {
      this.setEnsureEmailValiditySuccess();
    }

    return this.store.update((s) => ({ ...s, step2, emailIsValidated }));
  };

  submitStep3 = (step3Form: RegistrationStep3Form) =>
    this.store.update((s) => ({
      ...s,
      step3: {
        firstName: step3Form.firstName,
        lastName: step3Form.lastName,
      },
      cgu: step3Form.cgu || s.cgu,
    }));

  submitStep4 = (step4Form: RegistrationStep4Form) =>
    this.store.update((s) => ({
      ...s,
      step4: {
        badge: step4Form.badge,
        balance: step4Form.balance,
        contractType: step4Form.contractType,
        hasBadge: step4Form.hasBadge,
      },
      cgu: step4Form.cgu,
    }));

  getApiRegisterRequest(enforceBadgeNumber?: string): RegisterRequest | null {
    return this.store.query((x) => {
      if (!!x.site && !!x.step2 && !!x.step3 && !!x.cgu) {
        const req: RegisterRequest = {
          isUserRegistered: false,
          siteName: x.site.selectedSite!.siteName,
          email: x.step2.email,
          password: x.step2.password,
          firstName: x.step3.firstName,
          lastName: x.step3.lastName,
          hasNoBadge: !!x.step4 && x.step4.hasBadge === false,
          cgu: x.cgu,
          enforceBadgeNumber,
          badgeBalance: x.step4?.balance,
          badgeNumber: x.step4?.badge,
          contractType: x.step4?.contractType,
        };

        return req;
      }

      return null;
    });
  }

  registerSuccess(params: { resetStore: boolean }) {
    this.store.update(updateRequestStatus(RegistrationStatusKey.Register, 'success'));

    if (params.resetStore) this.store.reset();
  }

  cancelRegister() {
    this.store.update(updateRequestStatus(RegistrationStatusKey.Register, 'idle'));
  }

  setInvalidRegistrationRequest(err: AppError) {
    this.store.update(updateRequestStatus(RegistrationStatusKey.Register, 'error', err));
  }

  resetEnsureEmailValidityRequest() {
    this.store.update(updateRequestStatus(RegistrationStatusKey.EnsureEmailValidity, 'idle'));
  }

  setEnsureEmailValiditySuccess() {
    this.store.update(updateRequestStatus(RegistrationStatusKey.EnsureEmailValidity, 'success'));
  }

  sendVerificationEmail(): void {
    this.store.update(updateRequestStatus(RegistrationStatusKey.SendConfirmEmail, 'success'));
  }

  confirmEmail(): void {
    this.store.update(updateRequestStatus(RegistrationStatusKey.ConfirmEmail, 'success'));
  }

  unregister(): void {
    this.store.update(updateRequestStatus(RegistrationStatusKey.Unregister, 'success'));
  }

  public trackRequestStatus = createRequestsStatusOperator(this.store);
}

export const hasStep4 = (site?: RegistrationSiteDto) => {
  if (!site) return false;
  return site.hasBadgeRegistration || site.typeContratRequired || site.hasSoldeRegistration;
};
